"use client";
import { motion } from "framer-motion";
import { useTranslations } from "next-intl";
import React from "react";

// ============================================================================
// Root Section
// ============================================================================

export const WorkflowsSection = () => {
  return (
    <section className="dark relative w-full overflow-hidden bg-neutral-950">
      <SectionBackground />
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-20 md:py-28 lg:py-36">
        <BrandReveal />
        <div className="mt-12 lg:mt-20">
          <WorkflowFanout />
        </div>
        <div className="mt-14 lg:mt-24">
          <CapabilityGrid />
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// Background — dark band with hairlines
// ============================================================================

const SectionBackground = () => (
  <>
    <div className="absolute inset-x-0 top-0 h-px bg-neutral-800" />
    <div className="absolute inset-x-0 bottom-0 h-px bg-neutral-800" />
  </>
);

// ============================================================================
// Block 1 — Brand Reveal
// ============================================================================

const BrandReveal = () => {
  const t = useTranslations("workflows");
  return (
    <div className="flex flex-col items-center text-center">
      <p className="inline-flex items-center gap-1.5 text-sm font-semibold text-red-500">
        <BoltIcon className="h-3.5 w-3.5" />
        Workflows
      </p>

      <h2 className="mt-4 text-3xl md:text-5xl font-bold leading-tight tracking-tight text-white max-w-3xl">
        {t("tagline")}
      </h2>

      <p className="mt-5 text-sm md:text-base text-neutral-400 max-w-2xl leading-relaxed">
        {t("lead")}
      </p>
    </div>
  );
};

// ============================================================================
// Block 2 — Workflow Fan-out (Trigger ⊕ 4 Action Cards)
// ============================================================================

type ActionCfg = {
  key: 1 | 2 | 3 | 4;
  icon: React.ReactNode;
  delay: number;
};

const ACTIONS: ActionCfg[] = [
  { key: 1, icon: <IconMail />, delay: 0.6 },
  { key: 2, icon: <IconCalendar />, delay: 1.2 },
  { key: 3, icon: <IconPipeline />, delay: 1.8 },
  { key: 4, icon: <IconReminder />, delay: 2.4 },
];

const WorkflowFanout = () => {
  const t = useTranslations("workflows");
  return (
    <div className="relative rounded-2xl border border-neutral-800 bg-neutral-950 overflow-hidden p-6 md:p-10">

      {/* Trigger card */}
      <div className="relative z-10 max-w-md mx-auto">
        <div className="flex items-center gap-1.5 mb-2">
          <FlameIcon className="h-3.5 w-3.5 text-red-500" />
          <span className="text-[10px] font-semibold tracking-wider text-red-400 uppercase">
            {t("trigger_label")}
          </span>
        </div>
        <div className="rounded-xl border border-red-500/40 bg-neutral-900 p-4">
          <div className="text-sm font-semibold text-white">
            {t("trigger_status")}
          </div>
          <div className="text-[11px] text-neutral-400 mt-1">
            {t("trigger_meta")}
          </div>
        </div>
      </div>

      {/* Connector lines */}
      <div className="relative z-0 flex justify-center my-6 md:my-8" aria-hidden>
        <ConnectorLines />
      </div>

      {/* Actions header */}
      <div className="relative z-10 flex items-center justify-between mb-4">
        <div className="flex items-center gap-1.5">
          <BoltIcon className="h-3.5 w-3.5 text-red-500" />
          <span className="text-[10px] font-semibold tracking-wider text-red-400 uppercase">
            {t("actions_label")}
          </span>
        </div>
        <span className="text-[10px] text-neutral-500">{t("actions_meta")}</span>
      </div>

      {/* Action cards */}
      <div className="relative z-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {ACTIONS.map((a) => (
          <ActionCard
            key={a.key}
            icon={a.icon}
            title={t(`a${a.key}_title`)}
            desc={t(`a${a.key}_desc`)}
            doneLabel={t("status_done")}
            delay={a.delay}
          />
        ))}
      </div>
    </div>
  );
};

const ConnectorLines = () => (
  <svg
    viewBox="0 0 400 60"
    preserveAspectRatio="none"
    className="w-full max-w-2xl h-12 md:h-16"
    fill="none"
  >
    <defs>
      <linearGradient id="wfline" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0%" stopColor="rgba(239,68,68,0.6)" />
        <stop offset="100%" stopColor="rgba(239,68,68,0.05)" />
      </linearGradient>
    </defs>
    <path
      d="M200 0 L200 30 L50 30 L50 60"
      stroke="url(#wfline)"
      strokeWidth="1.5"
    />
    <path
      d="M200 0 L200 30 L150 30 L150 60"
      stroke="url(#wfline)"
      strokeWidth="1.5"
    />
    <path
      d="M200 0 L200 30 L250 30 L250 60"
      stroke="url(#wfline)"
      strokeWidth="1.5"
    />
    <path
      d="M200 0 L200 30 L350 30 L350 60"
      stroke="url(#wfline)"
      strokeWidth="1.5"
    />
  </svg>
);

const ActionCard = ({
  icon,
  title,
  desc,
  doneLabel,
  delay,
}: {
  icon: React.ReactNode;
  title: string;
  desc: string;
  doneLabel: string;
  delay: number;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.45, delay, ease: "easeOut" }}
    className="rounded-xl border border-neutral-800 bg-neutral-900/60 backdrop-blur p-4 flex flex-col gap-2"
  >
    <div className="flex items-center justify-between">
      <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-neutral-800 text-red-500">
        {icon}
      </span>
      <motion.span
        initial={{ opacity: 0, scale: 0.7 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3, delay: delay + 0.4, ease: "easeOut" }}
        className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-400 bg-emerald-950/60 border border-emerald-900 rounded px-1.5 py-0.5"
      >
        <CheckIcon className="h-3 w-3" />
        {doneLabel}
      </motion.span>
    </div>
    <div className="mt-1">
      <h4 className="text-sm font-semibold text-white">{title}</h4>
      <p className="mt-1 text-xs text-neutral-400 leading-relaxed">{desc}</p>
    </div>
  </motion.div>
);

// ============================================================================
// Block 3 — Capability Grid
// ============================================================================

type TileConfig = { key: string; icon: React.ReactNode };

const TILES: TileConfig[] = [
  { key: "t1", icon: <IconMail /> },
  { key: "t2", icon: <IconCalendar /> },
  { key: "t3", icon: <IconPipeline /> },
  { key: "t4", icon: <IconReminder /> },
];

const CapabilityGrid = () => {
  const t = useTranslations("workflows");
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {TILES.map((tile) => (
        <CapabilityTile
          key={tile.key}
          icon={tile.icon}
          label={t(`${tile.key}_label`)}
          desc={t(`${tile.key}_desc`)}
        />
      ))}
    </div>
  );
};

const CapabilityTile = ({
  icon,
  label,
  desc,
}: {
  icon: React.ReactNode;
  label: string;
  desc: string;
}) => (
  <div className="relative rounded-xl border border-neutral-800 bg-neutral-900/50 p-5">
    <div className="relative">
      <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-neutral-800 text-red-500">
        {icon}
      </div>
      <h3 className="mt-3 text-sm md:text-base font-semibold text-white">
        {label}
      </h3>
      <p className="mt-1 text-xs md:text-sm text-neutral-400 leading-relaxed">
        {desc}
      </p>
    </div>
  </div>
);

// ============================================================================
// Icons
// ============================================================================

function FlameIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden
    >
      <path d="M12 2.5c1.3 3.1 3.8 4.6 4.7 6.9.9 2.3.3 4.7-1.4 6.4-.6.6-1.3 1-2.1 1.2.5-.9.6-2 .2-3-.5-1.2-1.7-1.9-2.9-1.9-.3 1-.9 1.9-1.7 2.5-1.7 1.3-2.3 3.5-1.6 5.4.5 1.4 1.6 2.6 3 3-3.7-.2-7-3.1-7.4-6.9-.4-4.1 2.4-7.7 5.5-10.1C10.3 4.7 11.4 3.6 12 2.5z" />
    </svg>
  );
}

function BoltIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden
    >
      <path d="M13 2L4 14h6l-1 8 9-12h-6l1-8z" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="M4 12l5 5L20 6" />
    </svg>
  );
}

function IconMail() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden
    >
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3 7 9 6 9-6" />
    </svg>
  );
}

function IconCalendar() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden
    >
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M16 3v4M8 3v4M3 10h18" />
    </svg>
  );
}

function IconPipeline() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden
    >
      <rect x="3" y="4" width="5" height="16" rx="1" />
      <rect x="10" y="4" width="5" height="11" rx="1" />
      <rect x="17" y="4" width="4" height="7" rx="1" />
    </svg>
  );
}

function IconReminder() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden
    >
      <circle cx="12" cy="13" r="8" />
      <path d="M12 9v4l2.5 2.5M9 2h6" />
    </svg>
  );
}
